import { Validation } from './Validation';
import ContactJson, { defaultContactJson, validateEventBookingParticipantContactJsonAddNew, validateEventBookingParticipantContactJson, validateEventBookingMainContactJsonAddNew, validateEventBookingMainContactJson } from "./ContactJson";
import ClientJson from "./ClientJson";
import { EventBookingStatus } from "./EventBookingStatus";
import EventDateJson, { EventDateDesiredJson, defaultEventDateDesiredJson, validateEventDateJson, validateEventDateDesiredJson } from './EventDateJson'
import PriceJson, { PriceBookingJson, defaultPriceBookingJson, validateEventPriceBookingJson } from './PriceJson';
import CustomInputJson, { validateCustomInputJsonValue } from "./CustomInputJson";
import SepaMandateJson, { validateSepaMandate } from "./SepaMandateJson";
import InvoiceInfoJson from "./InvoiceInfoJson";
import PriceUtils from "./PriceUtils";

export default interface EventBookingJson {
   ID: number | null,

   /**
     * Telinehmende Personen an der Veranstaltung
     */
   participants: ContactJson[] | null;

   /**
     * Hauptkontakt zur Kommunikation. Oder erziehungsberechtigten Person (falls Teilnehmende unter 18)
     */
   contact: ContactJson | null;

   /**
     * Client/Account dieser Buchung.
     */
   client: ClientJson | null;

   /**
    * Freitextfeld für Krankheiten, Besonderheiten etc.
    */
   remarks: string;

   /**
    * Status der Buchung.
    */
   state: EventBookingStatus;

   /**
    * OfferBooking: Wunschtermine. Ist bei normalen Buchungen null.
    */
   desiredDates: EventDateDesiredJson[] | null;

   /**
    * OfferBooking: Finale Termine. Ist bei normalen Buchungen null.
    */
   bookedDates: EventDateJson[] | null;

   /**
    * OfferBooking: Anzahl der Teilnehmenden. Ist bei normalen Buchungen null.
    */
   participantCount: number | null;

   /**
    * Ausgewählter Preis.
    */
   selectedPrice: PriceBookingJson;

   /**
    * Ausgewählte zusätzliche Kosten/Optionen.
    */
   selectedAddonPrices: PriceBookingJson[];

   /**
    * Eingaben der individuellen Form Inputs
    */
   customInputs: CustomInputJson[];

   /**
    * Datum und Uhrzeit zu der gebucht wurde.
    */
   bookingDateTime: string;

   /**
    * SEPA-Lastschriftmandat für die Zahlung. Kann auch null sein, wenn eine Bar oder Rechnungszahlung gewünscht ist.
    */
   sepaMandate: SepaMandateJson | null;

   /**
     * Rechnungsinformationen wie Rechnungsnummer.
     */
   invoiceInfos: InvoiceInfoJson[] | null;
}

export type EventBookingCustomerViewJson = Omit<EventBookingJson, "client" | "bookingDateTime" | "sepaMandate" | "invoiceInfos"> & { "canStillBeModified": boolean; };

export function defaultEventBookingJson(): EventBookingJson {
  return {
    ID: null,
    participants: [{ ...defaultContactJson() }],
    contact: { ...defaultContactJson() },
    client: null,
    remarks: "",
    state: EventBookingStatus.requested,
    desiredDates: null,
    bookedDates: null,
    participantCount: null,
    selectedPrice: { ...defaultPriceBookingJson() },
    selectedAddonPrices: [],
    customInputs: [],
    bookingDateTime: new Date().toISOString(),
    sepaMandate: null,
    invoiceInfos: null,
  }
}

export function defaultEventOfferBookingJson(): EventBookingJson {
  return {
    ID: null,
    participants: null,
    contact: { ...defaultContactJson() },
    client: null,
    remarks: "",
    state: EventBookingStatus.requested,
    desiredDates: [{ ...defaultEventDateDesiredJson() }],
    bookedDates: [],
    participantCount: 1,
    selectedPrice: { ...defaultPriceBookingJson() },
    selectedAddonPrices: [],
    customInputs: [],
    bookingDateTime: new Date().toISOString(),
    sepaMandate: null,
    invoiceInfos: null,
  }
}

export function getEventBookingTotalPrice(price: PriceBookingJson, addonPrices: PriceBookingJson[], participantCount: number = 0) {
  var totalPrice = {
    priceDeposit: {
      priceIncludingVAT: 0,
      priceExcludingVAT: 0,
    },

    priceNonDeposit: {
      priceIncludingVAT: 0,
      priceExcludingVAT: 0,
    }
  }
  
  if(participantCount) {
    //deposit
    if (price.priceDepositParticipantCount && participantCount > price.priceDepositParticipantCount) {
      totalPrice.priceDeposit.priceIncludingVAT = price.priceDeposit + (participantCount - price.priceDepositParticipantCount) * price.priceDepositAdditionalParticipant;
      totalPrice.priceDeposit.priceExcludingVAT = PriceUtils.getPriceExcludingVAT(totalPrice.priceDeposit.priceIncludingVAT, price.priceDepositIncludedVATRate);
    } else {
      totalPrice.priceDeposit.priceIncludingVAT = price.priceDeposit * participantCount;
      totalPrice.priceDeposit.priceExcludingVAT = PriceUtils.getPriceExcludingVAT(totalPrice.priceDeposit.priceIncludingVAT, price.priceDepositIncludedVATRate);
    }

    //non deposit
    if (price.priceNonDepositParticipantCount && participantCount > price.priceNonDepositParticipantCount) {
      totalPrice.priceNonDeposit.priceIncludingVAT = price.priceNonDeposit + (participantCount - price.priceNonDepositParticipantCount) * price.priceNonDepositAdditionalParticipant;
      totalPrice.priceNonDeposit.priceExcludingVAT = PriceUtils.getPriceExcludingVAT(totalPrice.priceNonDeposit.priceIncludingVAT, price.priceNonDepositIncludedVATRate);
    } else {
      totalPrice.priceNonDeposit.priceIncludingVAT = price.priceNonDeposit * participantCount;
      totalPrice.priceDeposit.priceExcludingVAT = PriceUtils.getPriceExcludingVAT(totalPrice.priceNonDeposit.priceIncludingVAT, price.priceNonDepositIncludedVATRate);
    }

    //addons
    for(let addonPrice of addonPrices) {
      //addon deposit
      if (addonPrice.priceDepositParticipantCount && participantCount > addonPrice.priceDepositParticipantCount) {
        totalPrice.priceDeposit.priceIncludingVAT += addonPrice.priceDeposit + (participantCount - addonPrice.priceDepositParticipantCount) * addonPrice.priceDepositAdditionalParticipant;
        totalPrice.priceDeposit.priceExcludingVAT += PriceUtils.getPriceExcludingVAT(totalPrice.priceDeposit.priceIncludingVAT, addonPrice.priceDepositIncludedVATRate);
      } else {
        totalPrice.priceDeposit.priceIncludingVAT += addonPrice.priceDeposit * participantCount;
        totalPrice.priceDeposit.priceExcludingVAT += PriceUtils.getPriceExcludingVAT(totalPrice.priceDeposit.priceIncludingVAT, addonPrice.priceDepositIncludedVATRate);
      }

      //addon non deposit
      if (addonPrice.priceNonDepositParticipantCount && participantCount > addonPrice.priceNonDepositParticipantCount) {
        totalPrice.priceNonDeposit.priceIncludingVAT += addonPrice.priceNonDeposit + (participantCount - addonPrice.priceNonDepositParticipantCount) * addonPrice.priceNonDepositAdditionalParticipant;
        totalPrice.priceNonDeposit.priceExcludingVAT += PriceUtils.getPriceExcludingVAT(totalPrice.priceNonDeposit.priceIncludingVAT, addonPrice.priceNonDepositIncludedVATRate);
      } else {
        totalPrice.priceNonDeposit.priceIncludingVAT += addonPrice.priceNonDeposit * participantCount;
        totalPrice.priceNonDeposit.priceExcludingVAT += PriceUtils.getPriceExcludingVAT(totalPrice.priceNonDeposit.priceIncludingVAT, addonPrice.priceNonDepositIncludedVATRate);
      }
    }


  }

  return totalPrice;
}

export function validateEventBookingJsonManagementAdd(item: EventBookingJson) {
  return new Validation(item)
    .validateEquals("ID", null)
    .validateAllUsingFunction("participants", validateEventBookingParticipantContactJsonAddNew, 1)
    .validateUsingFunction("contact", validateEventBookingMainContactJsonAddNew, true)
    .validateString("remarks", 500)
    .validateEquals("state", EventBookingStatus.requested)
    .validateEquals("desiredDates", null)
    .validateEquals("bookedDates", null)
    .validateEquals("participantCount", null)
    .validateUsingFunction("selectedPrice", validateEventPriceBookingJson)
    .validateAllUsingFunction("selectedAddonPrices", validateEventPriceBookingJson)
    .validateAllUsingFunction("customInputs", validateCustomInputJsonValue)
    .validateDate("bookingDateTime")
    .validateUsingFunction("sepaMandate", validateSepaMandate, true)
    .finish();
}

export function validateEventBookingJsonManagement(item: EventBookingJson) {
  return new Validation(item)
    .validateInteger("ID", 0)
    .validateAllUsingFunction("participants", validateEventBookingParticipantContactJson, 1)
    .validateUsingFunction("contact", validateEventBookingMainContactJson, true)
    .validateString("remarks", 500)
    .validateEquals("desiredDates", null)
    .validateEquals("bookedDates", null)
    .validateEquals("participantCount", null)
    .validateUsingFunction("selectedPrice", validateEventPriceBookingJson)
    .validateAllUsingFunction("selectedAddonPrices", validateEventPriceBookingJson)
    .validateAllUsingFunction("customInputs", validateCustomInputJsonValue)
    .validateDate("bookingDateTime")
    .validateUsingFunction("sepaMandate", validateSepaMandate, true)
    .finish();
}

export function validateEventBookingJsonForm(item: EventBookingJson) {
  return new Validation(item)
    .validateEquals("ID", null)
    .validateAllUsingFunction("participants", validateEventBookingParticipantContactJsonAddNew, 1)
    .validateUsingFunction("contact", validateEventBookingMainContactJsonAddNew, true)
    .validateEquals("client", null)
    .validateString("remarks", 500)
    .validateEquals("state", EventBookingStatus.requested)
    .validateEquals("desiredDates", null)
    .validateEquals("bookedDates", null)
    .validateEquals("participantCount", null)
    .validateUsingFunction("selectedPrice", validateEventPriceBookingJson)
    .validateAllUsingFunction("selectedAddonPrices", validateEventPriceBookingJson)
    .validateAllUsingFunction("customInputs", validateCustomInputJsonValue)
    .validateDate("bookingDateTime")
    .validateUsingFunction("sepaMandate", validateSepaMandate, true)
    .finish();
}

export function validateEventOfferBookingJsonManagementAdd(item: EventBookingJson) {
  return new Validation(item)
    .validateEquals("ID", null)
    .validateEquals("participants", null)
    .validateUsingFunction("contact", validateEventBookingMainContactJsonAddNew, true)
    .validateString("remarks", 500)
    .validateEquals("state", EventBookingStatus.requested)
    .validateAllUsingFunction("desiredDates", validateEventDateDesiredJson, 1, false)
    .validateAllUsingFunction("bookedDates", validateEventDateJson, 1, false)
    .validateInteger("participantCount", 1, 150)
    .validateUsingFunction("selectedPrice", validateEventPriceBookingJson)
    .validateAllUsingFunction("selectedAddonPrices", validateEventPriceBookingJson)
    .validateAllUsingFunction("customInputs", validateCustomInputJsonValue)
    .validateDate("bookingDateTime")
    .validateUsingFunction("sepaMandate", validateSepaMandate, true)
    .finish();
}

export function validateEventOfferBookingJsonManagement(item: EventBookingJson) {
  return new Validation(item)
    .validateInteger("ID", 0)
    .validateEquals("participants", null)
    .validateUsingFunction("contact", validateEventBookingMainContactJson, true)
    .validateString("remarks", 500)
    .validateAllUsingFunction("desiredDates", validateEventDateDesiredJson, 1, false)
    .validateAllUsingFunction("bookedDates", validateEventDateJson, 1, false)
    .validateInteger("participantCount", 1, 150)
    .validateUsingFunction("selectedPrice", validateEventPriceBookingJson)
    .validateAllUsingFunction("selectedAddonPrices", validateEventPriceBookingJson)
    .validateAllUsingFunction("customInputs", validateCustomInputJsonValue)
    .validateDate("bookingDateTime")
    .validateUsingFunction("sepaMandate", validateSepaMandate, true)
    .finish();
}

export function validateEventOfferBookingJsonForm(item: EventBookingJson) {
  return new Validation(item)
    .validateEquals("ID", null)
    .validateEquals("participants", null)
    .validateUsingFunction("contact", validateEventBookingMainContactJsonAddNew, true)
    .validateEquals("client", null)
    .validateString("remarks", 500)
    .validateEquals("state", EventBookingStatus.requested)
    .validateAllUsingFunction("desiredDates", validateEventDateDesiredJson, 1, false)
    .validateAllUsingFunction("bookedDates", validateEventDateJson, 1, false)
    .validateInteger("participantCount", 1, 150)
    .validateUsingFunction("selectedPrice", validateEventPriceBookingJson)
    .validateAllUsingFunction("selectedAddonPrices", validateEventPriceBookingJson)
    .validateAllUsingFunction("customInputs", validateCustomInputJsonValue)
    .validateDate("bookingDateTime")
    .validateUsingFunction("sepaMandate", validateSepaMandate, true)
    .finish();
}

export function getEventBookingRequestMinDate(eventBookingsCanRequestUntilDaysAhead: number): Date {
  var stopDate = new Date();
  stopDate.setDate(stopDate.getDate() + eventBookingsCanRequestUntilDaysAhead + 1);
  stopDate.setHours(0, 0, 0);
  return stopDate;
}